import 'ag-grid-enterprise';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
  ValueFormatterParams
} from 'ag-grid-community';
import Transfer from '../../model/transfer.mjs';
import { Box, Button, Tooltip, useDisclosure } from '@chakra-ui/react';
import TransferDetail from './TransferDetail';
import StatusCellRenderer from './cell-renderers/StatusCellRenderer';
import {
  dateFormatter,
  dateFormatteArchiveDate,
  getFormattedFirearm,
  // dateFormatterInboundDate
} from './value-formatters/date-formatter.mjs';
import QueueDataSource from '@/queue/QueueDataSource';
import { ONLY_MY_TRANSFER_LABEL, QUEUE_GRID_TOOLTIP, SERVER_PAGE_SIZE } from '@/model/Constants';
import ModalComponent from '@/common/Modal';
import {
  getTranferDetails,
} from '@/api/apis';
import {
  ARCHIVE_HEADER_NAME,
  EMAIL_SEARCHING_LABEL,
  FFL_RECEIVER_DEALER_LABEL,
  FFL_SELLER_DEALER_LABEL,
  firearmType,
  STATUS,
  storeTypeFilters,
  SUB_STATUS,
  TRANSFER_DETAILS,
  TransferDetailsInitialState,
  transferMacrostatus,
  transferQueueSubStatusV2,
  transferType
} from '@/utils/constants';
import _, { isNull, isNumber } from 'lodash';
import {
  TransferCurrentNavigation,
  TransferDetailsType,
  TransferMacrostatus,
  TransferTypeInTransferList,
  keyType
} from '../types';
import {
  callToGetTransferStatusWorkFlow,
  setTransferFilterModel,
  setCreateTransferData,
  setIsRefreshingTransfers,
  setTransferCurrentNavigation,
  setIsExportTransferReportButtonDisabled,
  setTransferGridSortModel,
  setTransferCount,
  setTransferQueueLoading,
  setTransferSearchText,
  callToGetTransferFilters,
  setIsClearAllFilterClickedOnTransfer,
  setIsRemoveAnyFilter
} from '../ducks/slice';
import { useDispatch } from 'react-redux';
import { Key } from 'react-aria-components';
import logger from '@/utils/logger.mjs';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import {
  getCreateTransferData,
  getEcommerceStatusLists,
  getIsClearAllFilterClickedOnTransfer,
  getIsRefreshingTransfers,
  getIsRemoveAnyFilter,
  getSourceDescriptions,
  getStoreNamesData,
  getTransferCurrentNavigation,
  getTransferFilterPreferencesLoaded,
  getTransferGridSortModel,
  getTransferSearchText
} from '@/queue/ducks/selectors';
import { getTransferFilterModel } from '@/explore/ducks/selectors';
import {
  useSearchParams,
  useParams,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { TransferListCustomParams } from '@/queue/types';
import EditTransferModal from './EditTransferModal';
import moment from 'moment';
import { trimDate } from '@/queue/utils'
import { Text, Link } from '@chakra-ui/react';
import cookie from 'js-cookie';
import AppConfig from '@/utils/app-config.mjs';
import { getIsGlobalAdmin, getIsGlobalOperations, getIsImpersonating } from '@/redux/app-selectors';
import { getIsMultiTenantStore } from '@/storeAdmin/ducks/selectors';
import spacetime from 'spacetime';

const EMPTY_PLACEHOLDER = '—';

// Action colors for last note (same as dealer-logs action badges)
const ACTION_NOTE_STYLES: Record<string, string> = {
  Called: 'dealer-logs-action-badge dealer-logs-action-called',
  Emailed: 'dealer-logs-action-badge dealer-logs-action-emailed',
  'Profile Edit': 'dealer-logs-action-badge dealer-logs-action-profile-edit',
  System: 'dealer-logs-action-badge dealer-logs-action-system',
  Other: 'dealer-logs-action-badge dealer-logs-action-other'
};

function getActionClass(action: string): string {
  const normalized = action.trim();
  const key = Object.keys(ACTION_NOTE_STYLES).find(
    (k) => k.toLowerCase() === normalized.toLowerCase()
  );
  return key ? ACTION_NOTE_STYLES[key] : ACTION_NOTE_STYLES.Other;
}

function getActionDisplayLabel(action: string): string {
  const normalized = action.trim();
  const key = Object.keys(ACTION_NOTE_STYLES).find(
    (k) => k.toLowerCase() === normalized.toLowerCase()
  );
  if (key) return key;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

/** IANA zone → short generic label (ET, CT, PT, …). Spacetime normalizes input; Intl supplies abbrev. */
function getTimezoneAbbr(tz: string): string {
  if (!tz?.trim()) return '';
  try {
    const normalized = tz.trim().replace(/ /g, '_');
    const s = spacetime.now(normalized);
    if (!s.isValid()) return tz;
    const iana = s.timezone().name;
    if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: iana,
          timeZoneName: 'shortGeneric'
        }).formatToParts(new Date());
        const abbr = parts.find((p) => p.type === 'timeZoneName')?.value;
        if (abbr) return abbr;
      } catch {
        // ignore Intl errors for odd identifiers
      }
    }
    return iana;
  } catch {
    return tz;
  }
}

// Last Note cell: tooltip only when text is truncated; action part before " - " gets colored (same as dealer-logs)
const LastNoteCell = memo(function LastNoteCell(params: { value?: string }) {
  const value = params?.value;
  const ref = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const checkTruncated = useCallback(() => {
    const el = ref.current;
    if (!el || value == null) return;
    setShowTooltip(el.scrollWidth > el.clientWidth);
  }, [value]);

  useLayoutEffect(() => {
    checkTruncated();
  }, [checkTruncated]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(checkTruncated);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkTruncated]);

  if (value == null || value === '' || String(value).trim() === '' || value === EMPTY_PLACEHOLDER) {
    return <>{EMPTY_PLACEHOLDER}</>;
  }
  const displayValue = typeof value === 'string' ? value : String(value);
  const dashIndex = displayValue.indexOf(' - ');
  const hasAction = dashIndex > 0;
  const actionPart = hasAction ? displayValue.slice(0, dashIndex) : '';
  const restPart = hasAction ? displayValue.slice(dashIndex + 3) : displayValue;

  const inner = (
    <span ref={ref} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
      {hasAction ? (
        <>
          <span className={getActionClass(actionPart)} style={{ fontWeight: 700 }}>{getActionDisplayLabel(actionPart)}</span>
          {' - '}
          {restPart}
        </>
      ) : (
        displayValue
      )}
    </span>
  );
  if (!showTooltip) return inner;
  return (
    <Tooltip hasArrow placement="bottom" label={displayValue} color="black" bg="white">
      {inner}
    </Tooltip>
  );
});

interface Props {
  gridRef: React.RefObject<AgGridReact<Transfer>>;
  isSuperAdmin: boolean;
  cognitoGroup: boolean;
  isStoreAdmin: boolean;
  isStoreAgent?: boolean;
  transferType?: boolean;
}

export default function QueueGrid({
  gridRef,
  isSuperAdmin,
  isStoreAdmin,
  isStoreAgent,
  cognitoGroup,
  // transferType

}: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditTransferActive,
    onOpen: onOpenEditTransferActive,
    onClose: onCloseEditTransferActive
  } = useDisclosure();
  const [transfer, setTransfer] = useState<{ id: string } | null>(null);
  const [transferToBeEdit, setTransferToBeEdit] = useState<{
    id: string;
  } | null>();
  const [dynamicColumns, setDynamicColumns] = useState<ColDef[]>([]);
  const [isRecievingFFLColumn, setIsRecievingFFLColumn] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const transferDataSource = new QueueDataSource();
  const transferFilterModel = getTransferFilterModel();
  const storeList = getStoreNamesData();
  const sourceList = getSourceDescriptions();
  const eccomerceStatusList = getEcommerceStatusLists();
  const transferCurrentNavigation = getTransferCurrentNavigation();
  const transferSearchText = getTransferSearchText();
  const [navigateTo, setNavigateTo] = useState<string>("");

  const dispatch = useDispatch();
  const [searchParams, setURLSearchParams] = useSearchParams();
  const transfer_id = searchParams.get('transfer_id');
  const pending_transfer_id = searchParams.get('pending_transfer_id');
  const createTransferDetails = getCreateTransferData();
  const transferGridSortModel = getTransferGridSortModel() || [];
  const isFirstLoad = useRef(true);
  const [isColumnSet, setIsColumnSet] = useState<boolean>(false);
  const [isStoreNameInit, setIsStoreNameInit] = useState<boolean>(false);
  const previousFilterModel = useRef<any>(null);
  const isFirstLoadFilter = useRef(true);
  const initialDealerIdFromUrlAppliedRef = useRef(false);
  const isClearAllFilterClickedOnTransfer = getIsClearAllFilterClickedOnTransfer()
  const isRemoveAnyFilter = getIsRemoveAnyFilter()
  const [clipsBoxHeight, setClipsBoxHeight] = useState(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const isImpersonating = getIsImpersonating();
  const isGlobalOperations = getIsGlobalOperations();
  const isGlobalAdmin = getIsGlobalAdmin();
  const isMultiTenantStore = getIsMultiTenantStore();
  const transferFilterPreferencesLoaded = getTransferFilterPreferencesLoaded();
  const initialDatasourceSetRef = useRef(false);
  // const transferTypeRef = useRef(transferType);

  const isGlobalAdminImpersonating = () => {
    return cookie.get(AppConfig.cookieStoreIdName) && isImpersonating;
  }

  const onCloseTransferDetails = () => {
    onClose();
    searchParams.delete('transfer_id');
    setURLSearchParams(searchParams);
  };

  const onCloseEditTransferDetails = () => {
    onCloseEditTransferActive();
    searchParams.delete('pending_transfer_id');
    setURLSearchParams(searchParams);
  };

  useEffect(() => {
    dispatch(callToGetTransferFilters())
    return () => setNavigateTo("")
  }, [])

  useEffect(() => {
    if (navigateTo) navigate(navigateTo);
  }, [navigateTo]);

  useEffect(() => {
    if (transfer_id) {
      setTransfer({ id: transfer_id });
      onOpen();
    }
    if (pending_transfer_id) {
      setTransferToBeEdit({ id: pending_transfer_id });
      if (!isEditTransferActive) onOpenEditTransferActive();
    }
  }, [transfer_id, pending_transfer_id]);

  useEffect(() => {
    if (createTransferDetails && createTransferDetails.id) {
      setTransfer({ id: createTransferDetails.id });
      onOpen();
    }
  }, [createTransferDetails]);
  useEffect(() => {
    const status = searchParams.get('status');
    const licenseId = searchParams.get('license');
    const dealerId = searchParams.get('dealer_id');
    if (status && licenseId) {
      setTimeout(() => {
        const currentTransferFilterModel =
          transferFilterModelRef.current || transferFilterModel;
        dispatch(
          setTransferFilterModel({
            ...currentTransferFilterModel,
            'licensee.id': {
              filterType: 'text',
              type: 'equals',
              filter: licenseId
            }
          })
        );
      }, 1000);
    }
    if (!(transferCurrentNavigation === TransferCurrentNavigation.ARCHIVE)) {
      if (searchParams.get('archive') === 'true' && (isStoreAdmin || isStoreAgent)) {
        dispatch(
          setTransferCurrentNavigation(TransferCurrentNavigation.ARCHIVE)
        );
      } else {
        searchParams.delete('archive');
        setURLSearchParams(searchParams);
      }
    }
    if (dealerId) {
      const currentTransferFilterModel =
        transferFilterModelRef.current || transferFilterModel;
      dispatch(
        setTransferFilterModel({
          ...currentTransferFilterModel,
          searchText: {
            ...currentTransferFilterModel.searchText,
            filter: dealerId
          }
        })
      );
      dispatch(setTransferSearchText(dealerId));
    }
  }, [searchParams]);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
    } else {
      if (
        transferCurrentNavigation === TransferCurrentNavigation.TRANSFER_QUEUE
      ) {
        const updatedDynamicFIlters = [...dynamicColumns].filter(
          (filter) => filter.field !== 'archiveDate'
        );
        setDynamicColumns(updatedDynamicFIlters);
      }
      if (transferCurrentNavigation === TransferCurrentNavigation.ARCHIVE) {
        setDynamicColumns((prevState) => [
          ...prevState,
          {
            field: 'archiveDate',
            headerName: ARCHIVE_HEADER_NAME,
            valueFormatter: dateFormatteArchiveDate
          }
        ]);
      }
      if (
        gridRef?.current &&
        gridRef.current.api &&
        (transferCurrentNavigation === TransferCurrentNavigation.ARCHIVE ||
          transferCurrentNavigation ===
          TransferCurrentNavigation.TRANSFER_QUEUE)
      ) {
        const currentTransferFilterModel =
          transferFilterModelRef.current || transferFilterModel;
        dispatch(
          setTransferFilterModel({
            ...currentTransferFilterModel,
            searchText: {
              ...currentTransferFilterModel.searchText,
              filter: transferSearchText.trim()
            }
          })
        );
        if (transferFilterModel?.searchText?.filter.trim() === transferSearchText.trim())
          reFetchData();
      }
    }
  }, [transferCurrentNavigation]);
  //     useEffect(() => {
  //         if (transfer) {

  //             console.log("transfer_id",transfer.id);

  //             navigate(`transfer-details/602`)
  //         }
  // }, [transfer])

  // *** commented this for changes to be address later ***
  // useEffect(() => {
  //   const intervalTIme = 1000 * 60 * 5;
  //   const interval = setInterval(reFetchData, intervalTIme);

  //   return () => {
  //     if (interval) {
  //       clearInterval(interval);
  //     }
  //   };
  // }, []);

  // useEffect(() => {
  //   transferTypeRef.current = transferType;
  //   setIsColumnSet(true);
  // }, [transferType]);


  // useEffect(() => {
  //   if (gridRef.current && gridRef.current.api) {
  //     const newColumnDefs = transferType ? inboundColumnDefs : columnDefs;
  //     gridRef.current.api.updateGridOptions({
  //       columnDefs: [...newColumnDefs, ...dynamicColumns]
  //     });

  //     // Clear existing data and refresh with new transferType
  //     gridRef.current.api.setRowCount(0);
  //     gridRef.current.api.refreshInfiniteCache();
  //     const savedColumnStateKey = transferType ? `${columnStateKey}_inbound` : columnStateKey;
  //     // Reapply any saved column state if needed
  //     const savedState = localStorage.getItem(savedColumnStateKey);
  //     if (savedState) {
  //       const columnState = JSON.parse(savedState);
  //       gridRef.current.api.applyColumnState({ state: columnState, applyOrder: true });
  //     }
  //   }
  // }, [transferType, dynamicColumns]);

  const licenseeFormatter = ({ value }: ValueFormatterParams) => {
    return value ? value.toUpperCase() : '';
  };
  const ecommerceStatusFormatter = ({ value }: ValueFormatterParams) => {
    return value ? value : '-';
  };

  function renderDealerFormatter(data: any, columnName: string) {
    let value = '';
    let linkUrl = '';
    localStorage.setItem('emailNotFound', 'false');
    if (columnName === 'licensee.name') {
      linkUrl = `/dealers-search/${data?.data?.recieverDealerId}`;
      
      const isEmailSearch = ["queue_status_sms_sent_to_dealer", "queue_status_email_manually_searching"].includes(data?.data?.microStatus) && !(data?.data?.transferMetadata?.isActionPerformedBySupport);      
      if (isEmailSearch && (isGlobalAdmin || isGlobalOperations ||isImpersonating )) linkUrl += `?isEmailSearch=${isEmailSearch}`;
      
      const isEmailSearchForAdminAgent = ["queue_status_sms_sent_to_dealer", "queue_status_email_manually_searching", "queue_status_agent_action_for_transfer"].includes(data?.data?.microStatus)  && !(data?.data?.transferMetadata?.isActionPerformedByAgent);
      if (isEmailSearchForAdminAgent && (isStoreAdmin || isStoreAgent) && !isImpersonating) linkUrl += `?isEmailSearchForAdminAgent=${isEmailSearchForAdminAgent}`;
      
      value = (data) ? data?.data?.licensee?.name : "";
    }

    // Add this new condition for sellerLicenseKey
    if (columnName === 'sellerLicenseKey') {
      const dealerId = data?.data?.sellerDealerIdFromSellerLicenseKey;
      if (dealerId) {
        linkUrl = `/dealers-search/${dealerId}`;
        value = data?.value || data?.data?.sellerLicenseKey || "";
      } else {
        value = data?.value || data?.data?.sellerLicenseKey || "-";
      }
    }

    const text = (color: string) => (
      <Text color={color}>{value}</Text>
    );
    return (!value || value === '' || value === '-') ? (
      text('black')
    ) : (
      <Link onClick={(e) => {
        e.stopPropagation();
        window.open(linkUrl, '_blank');
      }} >
        {text('#0065FF')}
      </Link>
    );
  }

    const filterTypeForTransfers = () => {
      if(transferFilterModelRef?.current?.derivedStatus?.filterType === 'notEqual'){
        return  transferFilterModelRef?.current?.derivedStatus?.filterType
      }
      if(transferFilterModelRef?.current?.microStatus?.filterType === 'notEqual'){
        return transferFilterModelRef?.current?.microStatus?.filterType
      }
      return transferFilterModelRef?.current?.derivedStatus?.filterType || ''
    }
  const transferFilterModelRef = useRef(transferFilterModel);

  const createDataSource = (gridApi: GridReadyEvent<Transfer, any>['api']) => ({
    rowCount: undefined,
    getRows(params: TransferListCustomParams): void {
      const pageNumber = params.startRow / SERVER_PAGE_SIZE + 1;
      const urlParams = new URLSearchParams(window.location.search);
      if (pageNumber === 1) {
        dispatch(setTransferQueueLoading(true));
      }
      onBtHide();
      if (params['sortModel']?.length) {
        dispatch(setTransferGridSortModel(params['sortModel']));
      }
      if (!params['sortModel']?.length && transferGridSortModel?.length) {
        params['sortModel'] = transferGridSortModel;
      }

      const currentTransferFilterModel = transferFilterModelRef.current;
      let enhancedFilterModel = { ...params.filterModel };

      if (urlParams.get('dealer_id')) {
        enhancedFilterModel.searchText = {
          filterType: 'text',
          type: 'contains',
          filter: urlParams.get('dealer_id')
        };
      } else if (currentTransferFilterModel?.searchText?.filter?.trim()) {
        enhancedFilterModel.searchText = currentTransferFilterModel.searchText;
      }

      const licenseeIdFilter = currentTransferFilterModel?.['licensee.id'];
      if (
        licenseeIdFilter &&
        typeof licenseeIdFilter === 'object' &&
        'filter' in licenseeIdFilter &&
        String((licenseeIdFilter as { filter?: string }).filter ?? '').trim()
      ) {
        enhancedFilterModel['licensee.id'] = licenseeIdFilter as typeof enhancedFilterModel['licensee.id'];
      }

      if (currentTransferFilterModel?.referenceId?.filter?.trim()) {
        enhancedFilterModel.referenceId = currentTransferFilterModel.referenceId;
      }

      if ((currentTransferFilterModel?.sellerLicenseKey as any)?.values?.length > 0) {
        enhancedFilterModel.sellerLicenseKey = currentTransferFilterModel.sellerLicenseKey;
      }

      if (currentTransferFilterModel?.createdAt?.dateFrom && currentTransferFilterModel?.createdAt?.dateTo) {
        enhancedFilterModel.createdAt = currentTransferFilterModel.createdAt;
      }

      if ((currentTransferFilterModel?.storeName as any)?.values?.length > 0) {
        enhancedFilterModel.storeName = currentTransferFilterModel.storeName as any;
      }

      if ((currentTransferFilterModel?.firearm as any)?.values?.length > 0) {
        enhancedFilterModel.firearm = currentTransferFilterModel.firearm;
      }

      if ((currentTransferFilterModel?.derivedStatus as any)?.values?.length > 0) {
        enhancedFilterModel.derivedStatus = currentTransferFilterModel.derivedStatus;
      }

      if ((currentTransferFilterModel?.microStatus as any)?.values?.length > 0) {
        enhancedFilterModel.microStatus = currentTransferFilterModel.microStatus;
      }

      if ((currentTransferFilterModel?.agent as any)?.values?.length > 0) {
        enhancedFilterModel.agent = currentTransferFilterModel.agent;
      }

      if ((currentTransferFilterModel?.isArchive as any)?.values?.length > 0) {
        enhancedFilterModel.isArchive = currentTransferFilterModel.isArchive;
      }

      if ((currentTransferFilterModel?.linkedTransfers as any)?.values?.length > 0) {
        enhancedFilterModel.linkedTransfers = currentTransferFilterModel.linkedTransfers;
      }

      if ((currentTransferFilterModel?.ecommerce_status as any)?.values?.length > 0) {
        enhancedFilterModel.ecommerce_status = currentTransferFilterModel.ecommerce_status;
      }

      if ((currentTransferFilterModel?.source as any)?.values?.length > 0) {
        enhancedFilterModel.source = currentTransferFilterModel.source;
      }

      if ((currentTransferFilterModel?.transferType as any)?.values?.length > 0) {
        enhancedFilterModel.transferType = currentTransferFilterModel.transferType;
      }

      if ((currentTransferFilterModel?.is_external as any)?.values?.length > 0) {
        enhancedFilterModel.is_external = currentTransferFilterModel.is_external;
      }

      transferDataSource
        .fetchData({
          ...params,
          filterModel: enhancedFilterModel,
          startRow: pageNumber,
          type: filterTypeForTransfers() || '',
          ...(searchParams.get('status') === 'pending'
            ? { showPendingTransfers: true }
            : {}),
          ...(urlParams.get('archive') === 'true'
            ? { isArchive: true }
            : {})
        })
        .then((result) => {
          dispatch(
            setIsExportTransferReportButtonDisabled(
              result?.items?.length === 0
            )
          );
          let lastRow = result.meta.hasMore
            ? Number(gridApi.getDisplayedRowCount()) + SERVER_PAGE_SIZE
            : gridApi.getDisplayedRowCount() - 1 + result.items.length;
          if (result.items.length === 0 && gridApi.getDisplayedRowCount() <= 1) {
            onBtShowNoRows();
          } else if (result.items.length > 0) {
            gridRef.current?.api.hideOverlay();
          }

          params.successCallback(result.items, lastRow);
          if (gridApi.getDisplayedRowCount() !== 0)
            dispatch(setTransferCount(result?.meta?.resultCount));
          else
            dispatch(setTransferCount(0));
          setTimeout(() => {
            autoSizeAll(false);
          }, 100);
        })
        .catch((error) => {
          logger.error('Error while fetching rows for ag-Grid', error);
          params.failCallback();
          dispatch(setTransferQueueLoading(false));
        })
        .finally(() => {
          dispatch(setIsRefreshingTransfers(false));
          dispatch(setTransferQueueLoading(false));
        });
    }
  });

  const onGridReady = (gridEvent: GridReadyEvent<Transfer, any>) => {
    dispatch(setTransferCount(0));
    if (searchParams.get('dealer_id')) {
      initialDealerIdFromUrlAppliedRef.current = true;
    }
    if (transferFilterPreferencesLoaded) {
      gridEvent.api.setGridOption('datasource', createDataSource(gridEvent.api));
    }
  };

  useEffect(() => {
    if (!transferFilterPreferencesLoaded || !gridRef.current?.api || initialDatasourceSetRef.current) return;
    initialDatasourceSetRef.current = true;
    transferFilterModelRef.current = transferFilterModel;
    const { createdAt, ...restFilterModel } = transferFilterModel;
    const newFilterModel = {
      ...restFilterModel,
      createdAt:
        createdAt?.dateFrom && createdAt?.dateTo && new Date(createdAt.dateFrom) <= new Date(createdAt.dateTo)
          ? createdAt
          : {},
    };
    previousFilterModel.current = newFilterModel;
    const api = gridRef.current.api;
    // api.setFilterModel(newFilterModel);
    api.setGridOption('datasource', createDataSource(api));
  }, [transferFilterPreferencesLoaded, transferFilterModel]);
  useEffect(() => {
    if (gridRef.current && gridRef.current.api && eccomerceStatusList.length > 0) {
      // Get current column definitions
      const currentColumnDefs = gridRef.current.api.getColumnDefs() || [];

      // Update the e-commerce status column with new values
      const updatedColumnDefs = currentColumnDefs.map((colDef: any) => {
        if (colDef.field === 'ecommerce_status') {
          return {
            ...colDef,
            filterParams: {
              ...colDef.filterParams,
              values: eccomerceStatusList.map((ele: any) => ele.value),
            }
          };
        }
        return colDef;
      });

      // Update the grid with new column definitions
      gridRef.current.api.updateGridOptions({
        columnDefs: updatedColumnDefs
      });

      // // Force refresh the filter to pick up new values
      // const ecommerceColumn = gridRef.current.api.getColumn('ecommerce_status');
      // if (ecommerceColumn) {
      //   gridRef.current.api.destroyFilter('ecommerce_status');
      // }
    }
  }, [eccomerceStatusList]);


  const columnDefs: ColDef[] = [
    {
      field: 'derivedStatus',
      headerName: STATUS,
      cellRenderer: (params: ValueFormatterParams) => StatusCellRenderer(params),
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.keys(transferMacrostatus),
        valueFormatter: (params: ValueFormatterParams) => {
          return transferMacrostatus[
            params.value as keyType<typeof transferMacrostatus>
          ];
        }
      },
      cellRendererParams: { reFetchData, isStoreAdmin,isStoreAgent, gridRef }
    },
    {
      field: 'microStatus',
      headerName: SUB_STATUS,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: ValueFormatterParams) => {

        if (params?.data?.pendingDocuments &&  Array.isArray(params?.data?.pendingDocuments) && params?.data?.pendingDocuments?.length > 0) {
          return formatPendingDocuments(params.data.pendingDocuments);
        }

        return isNull(params.value) ? "-" : transferQueueSubStatusV2[
          params.value as keyType<typeof transferQueueSubStatusV2>
        ];
      },
      filterParams: {
        values: Object.keys(transferQueueSubStatusV2),
        valueFormatter: (params: ValueFormatterParams) => {
          return (
            transferQueueSubStatusV2[
            params.value as keyType<typeof transferQueueSubStatusV2>
            ] || 'NONE'
          );
        }
      }
    },
    {
      field: 'createdAt',
      headerName: 'Transfer Initiated',
      valueFormatter: dateFormatter
      // filter: 'agDateColumnFilter',
      // filterParams: {
      //   includeBlanksInRange: true
      // }
    },
    {
      field: 'age',
      colId: 'age',
      headerName: 'Age',
      headerTooltip: QUEUE_GRID_TOOLTIP.ageTooltip,
    },
    {
      field: 'referenceId',
      headerName: 'Order ID',
      filter: 'agTextColumnFilter',
      headerTooltip: QUEUE_GRID_TOOLTIP.orderNumberTooltip,
      filterParams: {
        filterOptions: ['equals', 'contains'],
        maxNumConditions: 1
      }
    },
    {
      field: 'sellerLicenseKey',
      colId: 'sellerLicenseKey',
      headerName: 'Seller License',
      filter: 'agSetColumnFilter',
      headerTooltip: 'Seller License Key',
      hide: false,
      valueFormatter: ({ value }: ValueFormatterParams) => {
        return value && typeof value === 'string' && value.trim() !== '' ? value : '-';
      },
      cellRenderer: (data: any) => renderDealerFormatter(data, 'sellerLicenseKey'),
      filterParams: {
        filterOptions: ['equals', 'contains'],
        maxNumConditions: 1
      }
    },
    {
      field: 'buyerName',
      colId: 'name',
      headerName: 'Buyer Name',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1
      }
    },
    {
      field: 'source',
      colId: 'source',
      headerName: 'Source',
      filterParams: {
        values: [
          ...sourceList.map((ele: any) => ele.value),
          null
        ].filter((value, index, self) => self.indexOf(value) === index),
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value;
        }
      },
      filter: 'agSetColumnFilter'
    },
    {
      field: 'ecommerce_status',
      // colId: 'ecommerce status',
      headerName: 'E-Commerce Status',
      filterParams: {
        // filterOptions: ['contains', 'startsWith', 'endsWith'],
        // maxNumConditions: 1,
        values: [
          ...eccomerceStatusList.map((ele: any) => ele.value),
          null
        ].filter((value, index, self) => self.indexOf(value) === index),
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value;
        },
        // defaultToNothingSelected: true,
        suppressSelectAll: false,
        excelMode: 'windows',
        refreshValuesOnOpen: true,
        clearButton: true,
      },
      valueFormatter: ecommerceStatusFormatter,
      filter: 'agSetColumnFilter',
    },
    {
      field: 'sellerName',
      headerName: FFL_SELLER_DEALER_LABEL,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        maxNumConditions: 1
      },
      valueFormatter: licenseeFormatter,
    },
    {
      field: 'sellerDisplayName',
      headerName: 'Seller Name',
      colId: 'sellerDisplayName',
      hide: true,
      lockVisible: !((isGlobalAdmin || isGlobalOperations) && !isImpersonating),
      valueGetter: (params) =>
        params.data?.sellerDisplayName ?? params.data?.seller_display_name ?? '',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        maxNumConditions: 1
      },
      valueFormatter: licenseeFormatter,
    },
    {
      field: 'sellerTimezone',
      headerName: 'Timezone',
      colId: 'sellerTimezone',
      hide: true,
      lockVisible: !((isGlobalAdmin || isGlobalOperations) && !isImpersonating),
      valueGetter: (params) =>
        params.data?.sellerTimezone ?? params.data?.seller_timezone ?? '',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        maxNumConditions: 1
      },
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value == null || typeof value !== 'string' || value.trim() === '')
          return '-';
        return getTimezoneAbbr(value);
      }
    },
    {
      field: 'licensee.name',
      headerName: FFL_RECEIVER_DEALER_LABEL,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        maxNumConditions: 1
      },
      valueFormatter: licenseeFormatter,
      cellRenderer: (data: any) => renderDealerFormatter(data, 'licensee.name')
    },
    {
      field: 'firearm', headerName: TRANSFER_DETAILS.FIREARM_TYPE,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.keys(firearmType).map((firearm) => firearm.toLowerCase()),
        valueFormatter: (params: ValueFormatterParams) => {
          return firearmType[
            params.value as keyType<typeof firearmType>
          ];
        }
      },
      valueFormatter: getFormattedFirearm
    },
    {
      filter: 'agSetColumnFilter',
      field: 'agent',
      headerName: 'Agent',
      filterParams: {
        values: [ONLY_MY_TRANSFER_LABEL],
        defaultToNothingSelected: true,
        suppressSelectAll: true,
        suppressMiniFilter: true
      }
    },
    {
      field: 'transferCode',
      headerName: 'Transfer Code',
      sortable: false,
      headerTooltip: QUEUE_GRID_TOOLTIP.transcodeTooltip,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        value ? value.toUpperCase() : value
    },
    {
      field: 'lastDealerEvent',
      headerName: 'Action + Last Note',
      colId: 'lastDealerEvent',
      hide: true,
      lockVisible: !((isGlobalAdmin || isGlobalOperations) && !isImpersonating),
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        maxNumConditions: 1
      },
      cellRenderer: LastNoteCell,
    },
    {
      field: 'searchText',
      hide: true,
      filter: 'agTextColumnFilter'
    }
  ];

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      // Determine if column should be shown
      const shouldShow = isMultiTenantStore || (isGlobalAdmin && !isImpersonating);
      const shouldShowForGlobalAdminOrOps = (isGlobalAdmin || isGlobalOperations) && !isImpersonating;

      // Use the API to set column visibility
      gridRef.current.api.setColumnVisible('sellerLicenseKey', shouldShow);
      gridRef.current.api.setColumnVisible('lastDealerEvent', shouldShowForGlobalAdminOrOps);
      gridRef.current.api.setColumnVisible('sellerDisplayName', shouldShowForGlobalAdminOrOps);
      gridRef.current.api.setColumnVisible('sellerTimezone', shouldShowForGlobalAdminOrOps);

      // Optional: Force refresh to ensure visibility updates
      gridRef.current.api.refreshHeader();
    }
  }, [isMultiTenantStore, isGlobalAdmin, isGlobalOperations, isImpersonating]);

  // const inboundColumnDefs: ColDef[] = [
  //   {
  //     field: 'status',
  //     headerName: STATUS,
  //     cellRenderer: (params: ValueFormatterParams) => StatusCellRenderer(params),
  //     filter: 'agSetColumnFilter',
  //     filterParams: {
  //       values: Object.keys(transferMacrostatus),
  //       valueFormatter: (params: ValueFormatterParams) => {
  //         return transferMacrostatus[
  //           params.value as keyType<typeof transferMacrostatus>
  //         ];
  //       }
  //     },
  //     cellRendererParams: { reFetchData, isStoreAdmin,isStoreAgent, gridRef }
  //   },
  //   {
  //     field: 'microStatus',
  //     headerName: SUB_STATUS,
  //     filter: 'agSetColumnFilter',
  //     cellRenderer: (params: ValueFormatterParams) => {

  //       if (params?.data?.pendingDocuments &&  Array.isArray(params?.data?.pendingDocuments) && params?.data?.pendingDocuments?.length > 0) {
  //         return formatPendingDocuments(params.data.pendingDocuments);
  //       }

  //       return isNull(params.value) ? "-" : transferQueueSubStatusV2[
  //         params.value as keyType<typeof transferQueueSubStatusV2>
  //       ];
  //     },
  //     filterParams: {
  //       values: Object.keys(transferQueueSubStatusV2),
  //       valueFormatter: (params: ValueFormatterParams) => {
  //         return (
  //           transferQueueSubStatusV2[
  //           params.value as keyType<typeof transferQueueSubStatusV2>
  //           ] || 'NONE'
  //         );
  //       }
  //     }
  //   },
  //   {
  //     field: 'createdAt',
  //     headerName: 'Transfer Initiated',
  //     valueFormatter: dateFormatter,
  //     filter: 'agDateColumnFilter',
  //     filterParams: {
  //       includeBlanksInRange: true
  //     }
  //   },
  //   {
  //     field: 'age',
  //     colId: 'age',
  //     headerName: 'Age',
  //     headerTooltip: QUEUE_GRID_TOOLTIP.ageTooltip,
  //   },
  //   {
  //     field: 'inboundTransferCompletionTime',
  //     headerName: 'Inbound Completion Date',
  //     valueFormatter: dateFormatterInboundDate,
  //   },
  //   {
  //     field: 'referenceId',
  //     headerName: 'Order ID',
  //     filter: 'agTextColumnFilter',
  //     headerTooltip: QUEUE_GRID_TOOLTIP.orderNumberTooltip,
  //     filterParams: {
  //       filterOptions: ['equals', 'contains'],
  //       maxNumConditions: 1
  //     }
  //   },
  //   {
  //     field: 'buyerName',
  //     colId: 'name',
  //     headerName: 'Buyer Name',
  //     filter: 'agTextColumnFilter',
  //     filterParams: {
  //       filterOptions: ['contains'],
  //       maxNumConditions: 1
  //     }
  //   },
  //   {
  //     field: 'source',
  //     colId: 'source',
  //     headerName: 'Source',
  //     filterParams: {
  //       values: [
  //         ...sourceList.map((ele: any) => ele.value),
  //         null
  //       ].filter((value, index, self) => self.indexOf(value) === index),
  //       valueFormatter: (params: ValueFormatterParams) => {
  //         return params.value;
  //       }
  //     },
  //     filter: 'agSetColumnFilter'
  //   },
  //   {
  //     field: 'sellerName',
  //     headerName: FFL_SELLER_DEALER_LABEL,
  //     filter: 'agTextColumnFilter',
  //     filterParams: {
  //       filterOptions: ['contains', 'startsWith', 'endsWith'],
  //       maxNumConditions: 1
  //     },
  //     valueFormatter: licenseeFormatter,
  //   },
  //   {
  //     field: 'firearm',
  //     headerName: TRANSFER_DETAILS.FIREARM_TYPE,
  //     filter: 'agSetColumnFilter',
  //     filterParams: {
  //       values: Object.keys(firearmType).map((firearm) => firearm.toLowerCase()),
  //       valueFormatter: (params: ValueFormatterParams) => {
  //         return firearmType[
  //           params.value as keyType<typeof firearmType>
  //         ];
  //       }
  //     },
  //     valueFormatter: getFormattedFirearm
  //   },
  //   {
  //     field: 'transferCode',
  //     headerName: 'Transfer Code',
  //     sortable: false,
  //     headerTooltip: QUEUE_GRID_TOOLTIP.transcodeTooltip,
  //     valueFormatter: ({ value }: ValueFormatterParams) =>
  //       value ? value.toUpperCase() : value
  //   },
  //   {
  //     field: 'isArchive',
  //     headerName: TRANSFER_DETAILS.ARCHIVED,
  //     sortable: false,
  //     filter: 'agSetColumnFilter',
  //     filterParams: {
  //       values: Object.keys(storeTypeFilters),
  //       valueFormatter: (params: ValueFormatterParams) => {
  //         return params.value === "true" ? "Yes" : "No"
  //       }
  //     },
  //     valueFormatter: ({ value }: ValueFormatterParams) => {
  //       return value ? "Yes" : value === false ? "No" : ''
  //     },
  //   },
  //   {
  //     field: 'searchText',
  //     hide: true,
  //     filter: 'agTextColumnFilter'
  //   }
  // ];

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true
    }),
    []
  );
  const columnStateKey = (isSuperAdmin || isGlobalOperations) ? 'globalAdminColumnState' : 'adminColumnState';

  const onColumnStateChanged = useCallback(() => {
    if (gridRef.current && isColumnSet) {
      // Don't save state if we're currently resetting columns
      const resetFlagKey = `${columnStateKey}_resetting`;
      const isResetting = localStorage.getItem(resetFlagKey) === 'true';

      if (!isResetting) {
        const columnState = gridRef.current.api.getColumnState();
        localStorage.setItem(columnStateKey, JSON.stringify(columnState));
      }
    }
  }, [isColumnSet, columnStateKey]);

  // const onColumnStateChanged = useCallback(() => {
  //   if (gridRef.current && isColumnSet) {
  //     const columnState = gridRef.current.api.getColumnState();
  //     const typeColumnStateKey = transferType ? `${columnStateKey}_inbound` : columnStateKey;
  //     localStorage.setItem(typeColumnStateKey, JSON.stringify(columnState));
  //   }
  // }, [isColumnSet, transferType]);

  // const currentColumnDefs = useMemo(() => {
  //   return transferType ? inboundColumnDefs : columnDefs;
  // }, [transferType]);

  const gridStyle = useMemo(() => ({ width: '100%' }), []);

  let clickedIndex: any;
  const gridOptions: GridOptions<Transfer> = {
    rowModelType: 'infinite',
    infiniteInitialRowCount: 1,
    autoSizeStrategy: {
      type: 'fitCellContents'
    },
    // maxBlocksInCache: 3,
    maxConcurrentDatasourceRequests: 2,
    cacheBlockSize: SERVER_PAGE_SIZE,
    cacheOverflowSize: 2,
    tooltipShowDelay: 0,

    // generate a unique ID for each row based on the abuse ID
    getRowId: (params) => {
      return params.data.id || '';
    },
    getRowStyle:(params) => {
      if(params?.data?.is_reassigned && params?.data?.derivedStatus !== 'outbound_transfer_complete'){
        return {'background-color': 'rgba(255, 0, 0, 0.15)'};
      }
    },
    // column definitions
    // columnDefs: currentColumnDefs,
    columnDefs,
    defaultColDef,

    // row options
    animateRows: true,
    suppressRowClickSelection: true,
    onCellClicked: (event: any) => {
      if (event?.column.getColId() === 'licensee.name' || event?.column.getColId() === 'sellerLicenseKey')
        setIsRecievingFFLColumn(true)
      else
        setIsRecievingFFLColumn(false)
    },
    onRowClicked: (event: RowClickedEvent<Transfer>) => {
      // Prevent row click action if clicking on a link (sellerLicenseKey or licensee.name columns)
      const target = event.event?.target as HTMLElement;
      if (target && (target.tagName === 'A' || target.closest('a'))) {
        return; // Exit if clicking on a link
      }
       if (isRecievingFFLColumn) {
        return; // Don't do anything if clicking on link columns
      }

      if (gridRef.current && event.node) {
        gridRef.current.api.deselectAll();
        event.node.setSelected(true);
      }
      if (
        event.data &&
        event?.data?.derivedStatus === TransferMacrostatus.tranfer_pending
      ) {
        let params = {};
        const urlParams = new URLSearchParams(window.location.search);
        for (let [key, value] of urlParams.entries()) {
          params = { ...params, [key]: value };
        }
        setURLSearchParams({
          ...params,
          pending_transfer_id: event.data?.id as string
        });
        setTransferToBeEdit({ id: event.data?.id as string });
        if (!isEditTransferActive) onOpenEditTransferActive()
      } else {
        window.open(`/transfers/${event.data?.id as string}`, '_blank');
      }
    },
    // cell detail options
    suppressCellFocus: true,
    onGridReady,
    // misc options
    debug: false,
    icons: agGridIconsConfig,
    // sideBar: 'filters'
    overlayNoRowsTemplate:
      '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No FFL Transfers found</p><p class="not-found-box__message">Try adjusting your search or filter to find what you’re looking for</p></div>'
  };

  function reFetchData() {
    gridRef.current?.api.hideOverlay();
    gridRef.current?.api.setRowCount(0);
    gridRef.current?.api?.refreshInfiniteCache();
  }

  useEffect(() => {
    // Only refresh when datasource is set (after filter preferences are loaded)
    if (gridRef.current?.api?.getGridOption('datasource')) {
      const refreshGrid = () => {
        gridRef.current?.api.hideOverlay();
        gridRef.current?.api.setRowCount(0);
        gridRef.current?.api.refreshInfiniteCache();

        setTimeout(() => {
          if (gridRef.current?.api) {
            const currentDatasource = gridRef.current.api.getGridOption('datasource');
            if (currentDatasource) {
              gridRef.current.api.setGridOption('datasource', currentDatasource);
            }
          }
        }, 100);
      };

      refreshGrid();
    }
  }, []);

  useEffect(() => {
    const hasDatasource = gridRef.current?.api?.getGridOption('datasource');
    const canApplyFilters =
      storeList.length > 0 ||
      (!isSuperAdmin && !isGlobalOperations) ||
      hasDatasource;
    if (canApplyFilters && gridRef.current && gridRef.current.api) {
      const { createdAt, ...restFilterModel } = transferFilterModel;
      const newFilterModel = {
        ...restFilterModel,
        createdAt:
          createdAt?.dateFrom &&
            createdAt?.dateTo &&
            new Date(createdAt?.dateFrom) <= new Date(createdAt?.dateTo)
            ? createdAt
            : {},
      };
      const hasFilters = Object.values(newFilterModel).some((filter: any) => {
        if (filter.filterType === "set") return filter.values?.length > 0;
        if (filter.filterType === "text") return filter.filter?.trim() !== "";
        return false;
      });
      // Skip API call on first render (when no filters are applied)
      if (isFirstLoadFilter.current && !hasFilters && !isClearAllFilterClickedOnTransfer && !isRemoveAnyFilter) {
        isFirstLoadFilter.current = false;
        return;
      }

      const currentRowCount = gridRef.current.api.getDisplayedRowCount();
      const filterActuallyChanged = JSON.stringify(previousFilterModel.current) !== JSON.stringify(newFilterModel);
      const dealerIdFromUrl = searchParams.get('dealer_id');
      const isInitialDealerIdSync =
        Boolean(dealerIdFromUrl) &&
        initialDealerIdFromUrlAppliedRef.current &&
        newFilterModel?.searchText?.filter === dealerIdFromUrl &&
        filterActuallyChanged;

      if (filterActuallyChanged || isStoreNameInit) {
        if (isInitialDealerIdSync) {
          previousFilterModel.current = newFilterModel;
          transferFilterModelRef.current = transferFilterModel;
          initialDealerIdFromUrlAppliedRef.current = false;
        } else {
          if (filterActuallyChanged) {
            const prevCreatedAt = previousFilterModel.current?.createdAt;
            const nextCreatedAt = newFilterModel.createdAt;
            const createdAtChanged =
              JSON.stringify(prevCreatedAt ?? {}) !==
              JSON.stringify(nextCreatedAt ?? {});
            // Date range lives in Redux and is merged in datasource getRows; createdAt has no
            // ag-Grid column filter, so setFilterModel alone often does not reload. Refresh only
            // when the date range changed to avoid double API calls for other filters.
            transferFilterModelRef.current = transferFilterModel;
            gridRef.current.api.setFilterModel(newFilterModel);
            previousFilterModel.current = newFilterModel;
            if (
              createdAtChanged &&
              !isClearAllFilterClickedOnTransfer &&
              !isRemoveAnyFilter
            ) {
              gridRef.current.api.hideOverlay();
              gridRef.current.api.setRowCount(0);
              gridRef.current.api.refreshInfiniteCache();
            }
          } else if (isStoreNameInit && currentRowCount === 0) {
            gridRef.current.api.setRowCount(0);
            gridRef.current.api.refreshInfiniteCache();
          } else if (isClearAllFilterClickedOnTransfer || isRemoveAnyFilter) {
            gridRef.current.api.setRowCount(0);
            gridRef.current.api.refreshInfiniteCache();
          }
        }
      }

      if (isClearAllFilterClickedOnTransfer)
        dispatch(setIsClearAllFilterClickedOnTransfer(false))
      if(isRemoveAnyFilter){
        dispatch(setIsRemoveAnyFilter(false))
      }
    }
    transferFilterModelRef.current = transferFilterModel;
  }, [transferFilterModel, storeList, isStoreNameInit]);

  useEffect(() => {
    // Force refresh when ecommerce filter specifically changes
    const ecommerceFilter = transferFilterModel?.ecommerce_status;
    if (gridRef.current && gridRef.current.api && ecommerceFilter?.values?.length === 0) {
      // Force data refresh
      setTimeout(() => {
        const ecommerceColumn = gridRef?.current?.api?.getColumn('ecommerce_status');
        if (ecommerceColumn) {
          gridRef?.current?.api?.destroyFilter('ecommerce_status');
        }
      }, 100);
    }
  }, [transferFilterModel?.ecommerce_status]);

  const onBtShowNoRows = useCallback(() => {
    gridRef.current?.api.showNoRowsOverlay();
  }, []);

  function onBtHide() {
    gridRef.current?.api.hideOverlay();
  }
  const autoSizeAll = useCallback((skipHeader: boolean) => {
    const allColumnIds: Array<string> = [];
    gridRef.current?.api?.getColumns()?.forEach((column) => {
      allColumnIds.push(column.getId());
    });
    gridRef.current?.api?.autoSizeColumns(allColumnIds, skipHeader);
  }, []);

  useEffect(() => {
    if (!isSuperAdmin && !isGlobalOperations) {
      setIsColumnSet(false);
    }
    if (gridRef.current && gridRef.current.api) {
      const resetFlagKey = `${columnStateKey}_resetting`;
      const isResetting = localStorage.getItem(resetFlagKey) === 'true';

      // Don't apply saved state if we're currently resetting columns
      if (!isResetting) {
        const savedState = localStorage.getItem(columnStateKey);
        // const baseColumnDefs = transferType ? inboundColumnDefs : columnDefs;
        // const savedColumnStateKey = transferType ? `${columnStateKey}_inbound` : columnStateKey;
        // const savedState = localStorage.getItem(savedColumnStateKey);
        gridRef.current.api.updateGridOptions({
          'columnDefs': [
            // ...baseColumnDefs,
            ...columnDefs,
            ...dynamicColumns
          ]
        });
        gridRef.current.api.moveColumns(['archiveDate'], 3);
        gridRef.current.api.moveColumns(['transferType'], 0);
        gridRef.current.api.moveColumns(['storeName'], 2);
        if (!isSuperAdmin && !isGlobalOperations) {
          const completionTimeCol = transferCurrentNavigation === TransferCurrentNavigation.ARCHIVE ? 5 : 4;
          gridRef.current.api.moveColumns(['transferCompletionTime'], completionTimeCol);
        }
        if (savedState && savedState !== null) {
          const columnState = JSON.parse(savedState);
          gridRef.current.api.applyColumnState({ state: columnState, applyOrder: true });
        }
      } else {
        // If resetting, just update column defs without applying saved state
        gridRef.current.api.updateGridOptions({
          'columnDefs': [
            // ...baseColumnDefs,
            ...columnDefs,
            ...dynamicColumns
          ]
        });
        gridRef.current.api.moveColumns(['archiveDate'], 3);
        gridRef.current.api.moveColumns(['transferType'], 0);
        gridRef.current.api.moveColumns(['storeName'], 2);
        if (!isSuperAdmin && !isGlobalOperations) {
          const completionTimeCol = transferCurrentNavigation === TransferCurrentNavigation.ARCHIVE ? 5 : 4;
          gridRef.current.api.moveColumns(['transferCompletionTime'], completionTimeCol);
        }
      }
      // Always apply visibility logic (after restoring state if it exists)
      const shouldShow = isMultiTenantStore || (isGlobalAdmin && !isImpersonating);
      const shouldShowForGlobalAdminOrOps = (isGlobalAdmin || isGlobalOperations) && !isImpersonating;
      gridRef.current.api.setColumnVisible('sellerLicenseKey', shouldShow);
      gridRef.current.api.setColumnVisible('lastDealerEvent', shouldShowForGlobalAdminOrOps);
      gridRef.current.api.setColumnVisible('sellerDisplayName', shouldShowForGlobalAdminOrOps);
      gridRef.current.api.setColumnVisible('sellerTimezone', shouldShowForGlobalAdminOrOps);
      if (!isSuperAdmin && !isGlobalOperations) {
        setIsColumnSet(true);
      }
    }
  }, [dynamicColumns]);//[dynamicColumns, transferType])


  useEffect(() => {
    setIsColumnSet(false);
    setIsStoreNameInit(false);
    if ((storeList.length > 0 || sourceList.length > 0) && gridRef.current && gridRef.current.api) {
      const savedState = localStorage.getItem(columnStateKey);
      // const savedColumnStateKey = transferType ? `${columnStateKey}_inbound` : columnStateKey;
      // const savedState = localStorage.getItem(savedColumnStateKey);
      const adminColumns: ColDef[] = (isSuperAdmin) ? [{
        field: 'storeName',
        headerName: 'Store Name',
        filterParams: {
          values: storeList.map((ele: any) => ele.value),
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value;
          }
        },
        filter: 'agSetColumnFilter'
      },
      {
        field: 'inboundTransferCompletionTime',
        headerName: 'Inbound Transfer Complete',
        valueFormatter: ({ data }: ValueFormatterParams) => data?.inboundTransferCompletionTime ? moment(trimDate(data?.inboundTransferCompletionTime)).format('MM/DD/YYYY') : '-'
      },
      {
        field: 'outboundTransferCompletionTime',
        headerName: 'Outbound Completion Date',
        valueFormatter: ({ data }: ValueFormatterParams) => data?.outboundTransferCompletionTime ? moment(trimDate(data?.outboundTransferCompletionTime)).format('MM/DD/YYYY') : '-'
      },
      {
        // field: !transferType ? 'is_external' : 'storeType',
        field: 'is_external',
        headerName: 'Store Type',
        sortable: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: Object.keys(storeTypeFilters),
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value === "true" ? "Live" : "Testing"
          }
        },
        valueFormatter: ({ value }: ValueFormatterParams) => {

          return value ? "Live" : value === false ? "Testing" : ''
        },
        cellRendererParams: { reFetchData, isStoreAdmin, gridRef }
      },
      {
        field: 'isArchive',
        headerName: TRANSFER_DETAILS.ARCHIVED,
        sortable: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: Object.keys(storeTypeFilters),
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value === "true" ? "Yes" : "No"
          }
        },
        valueFormatter: ({ value }: ValueFormatterParams) => {
          return value ? "Yes" : value === false ? "No" : ''
        },
      },
      ] : [];
      // const inboundAdminColumns: ColDef[] = (isSuperAdmin) ? [{
      //   field: 'storeName',
      //   headerName: 'Store Name',
      //   filterParams: {
      //     values: storeList.map((ele: any) => ele.value),
      //     valueFormatter: (params: ValueFormatterParams) => {
      //       return params.value;
      //     }
      //   },
      //   filter: 'agSetColumnFilter'
      // },
      // {
      //   field: 'licensee.name',
      //   headerName: FFL_RECEIVER_DEALER_LABEL,
      //   filter: 'agTextColumnFilter',
      //   filterParams: {
      //     filterOptions: ['contains', 'startsWith', 'endsWith'],
      //     maxNumConditions: 1
      //   },
      //   valueFormatter: licenseeFormatter,
      //   cellRenderer: (data: any) => renderDealerFormatter(data, 'licensee.name')
      // },
      // {
      //   field: !transferType ? 'is_external' : 'storeType',
      //   headerName: 'Store Type',
      //   sortable: false,
      //   filter: 'agSetColumnFilter',
      //   filterParams: {
      //     values: Object.keys(storeTypeFilters),
      //     valueFormatter: (params: ValueFormatterParams) => {
      //       return params.value === "true" ? "Live" : "Testing"
      //     }
      //   },
      //   valueFormatter: ({ value }: ValueFormatterParams) => {

      //     return value ? "Live" : value === false ? "Testing" : ''
      //   },
      //   cellRendererParams: { reFetchData, isStoreAdmin, gridRef }
      // },
      // ] : [];
      let filteredColumns: ColDef[] = []
      // const typeAdminColumns = transferType ? inboundAdminColumns : adminColumns;

      // if (savedState && typeAdminColumns && typeAdminColumns.length > 0) {
        if (savedState && adminColumns && adminColumns.length > 0) {
        const columnState = JSON.parse(savedState);
        filteredColumns = adminColumns.filter((mainCol: any) =>
          columnState.some((refCol: any) => refCol.colId === mainCol.field)
        );
      } else {
        filteredColumns = adminColumns;
      }
      // const currentBaseColumns = transferType ? inboundColumnDefs : columnDefs;
      if(isGlobalOperations){
        // let isPresentInColumnDefs = null;
        // isPresentInColumnDefs = currentBaseColumns?.find(column => column?.field ==='storeName') || null;
        const isPresentInColumnDefs = columnDefs?.find(column => column?.field ==='storeName') || null;
        const isPresentInfilteredColumn = filteredColumns?.find(column => column?.field ==='storeName') || null;
        if(!isPresentInColumnDefs && !isPresentInfilteredColumn){
          filteredColumns.push({
            field: 'storeName',
            headerName: 'Store Name',
            filterParams: {
              values: storeList.map((ele: any) => ele.value),
              valueFormatter: (params: ValueFormatterParams) => {
                return params.value;
              }
            },
            filter: 'agSetColumnFilter'
          })
        }

        // const typeKey = transferType ? 'is_external' : 'storeType'
        // const isPresentInColumnDefsStoreType = currentBaseColumns?.find(column => column?.field ===typeKey) || null;
        // const isPresentInfilteredColumnStoreType = filteredColumns?.find(column => column?.field ===typeKey) || null;

        const isPresentInColumnDefsStoreType = columnDefs?.find(column => column?.field ==='storeType') || null;
        const isPresentInfilteredColumnStoreType = filteredColumns?.find(column => column?.field ==='storeType') || null;

        if(!isPresentInColumnDefsStoreType && !isPresentInfilteredColumnStoreType){
          filteredColumns.push({
            // field: !transferType ? 'is_external' : 'storeType',
            field: 'is_external',
            headerName: 'Store Type',
            sortable: false,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: Object.keys(storeTypeFilters),
              valueFormatter: (params: ValueFormatterParams) => {
                return params.value === "true" ? "Live" : "Testing"
              }
            },
            valueFormatter: ({ value }: ValueFormatterParams) => {

              return value ? "Live" : value === false ? "Testing" : ''
            },
            cellRendererParams: { reFetchData, isStoreAdmin, gridRef }
          })
        }
      }
      // const baseColumnDefs = transferType ? inboundColumnDefs : columnDefs;
      gridRef.current.api.updateGridOptions({
        'columnDefs': [
          // ...baseColumnDefs,
          ...columnDefs,
          ...dynamicColumns,
          ...filteredColumns,
        ]
      });
      // if(transferType){
      //   gridRef.current.api.moveColumns(['storeName'], 3);
      //   gridRef.current.api.moveColumns(['inboundTransferCompletionTime'], 5);
      //   gridRef.current.api.moveColumns(['licensee.name'], 10);
      // }else{
      //   gridRef.current.api.moveColumns(['storeName'], 3);
      //   gridRef.current.api.moveColumns(['inboundTransferCompletionTime'], 5);
      //   gridRef.current.api.moveColumns(['outboundTransferCompletionTime'], 6);
      // }
      // Check if we're resetting columns - if so, don't apply saved state
      const resetFlagKey = `${columnStateKey}_resetting`;
      const isResetting = localStorage.getItem(resetFlagKey) === 'true';

      if (!isResetting && savedState && savedState !== null) {
        const columnState = JSON.parse(savedState);
        gridRef.current.api.applyColumnState({ state: columnState, applyOrder: true });
      }
      // Always apply visibility logic (after restoring state if it exists)
      const shouldShow = isMultiTenantStore || (isGlobalAdmin && !isImpersonating);
      const shouldShowForGlobalAdminOrOps = (isGlobalAdmin || isGlobalOperations) && !isImpersonating;
      gridRef.current.api.setColumnVisible('sellerLicenseKey', shouldShow);
      gridRef.current.api.setColumnVisible('lastDealerEvent', shouldShowForGlobalAdminOrOps);
      gridRef.current.api.setColumnVisible('sellerDisplayName', shouldShowForGlobalAdminOrOps);
      gridRef.current.api.setColumnVisible('sellerTimezone', shouldShowForGlobalAdminOrOps);
      setTimeout(() => {
        setIsColumnSet(true);
      }, 200)
      setIsStoreNameInit(true);
    }
  }, [storeList, sourceList]); //[storeList, sourceList, transferType]

  useEffect(() => {
    // if (!isSuperAdmin && !isGlobalOperations && !transferType) {
      if (!isSuperAdmin && !isGlobalOperations) {
      setDynamicColumns((prevState) => [
        {
          field: 'transferCompletionTime',
          headerName: 'Transfer Completed',
          valueFormatter: ({ data }: ValueFormatterParams) => data?.transferCompletionTime ? moment(data?.transferCompletionTime).format('MM/DD/YYYY') : '-',
        },
        ...prevState
      ]);
    }
    // if(transferType){
    //   setDynamicColumns((prevState) => [
    //     ...prevState.filter((col) => col.field !== 'transferCompletionTime')
    //   ]);
    // }
  }, [isSuperAdmin,isGlobalOperations]) //[isSuperAdmin,isGlobalOperations, transferType]



  // Add this effect before the return statement
  useEffect(() => {
    const clipsBox = document.querySelector('.outer-clips-box');

    if (!clipsBox) {
      setClipsBoxHeight(isGlobalAdminImpersonating() ? 41 : 0);
      return;
    }

    // Create observer only once
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(_.debounce((entries) => {
        const entry = entries[0];
        if (entry) {
          setClipsBoxHeight(isGlobalAdminImpersonating() ? 41 + entry.contentRect.height : entry.contentRect.height);
        }
      }, 150)); // Debounce resize updates
    }

    resizeObserverRef.current.observe(clipsBox);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);


  return (
    <Box
    sx={{
      height: isStoreAdmin
        ? `calc(100vh - ${348 + clipsBoxHeight}px) !important`
        : `calc(100vh - ${314 + clipsBoxHeight}px) !important`,
    }}
      className={`Queue `}
      style={gridStyle}
    >
      <AgGridReact
        className={'ag-theme-alpine'}
        rowSelection="single"
        ref={gridRef}
        gridOptions={gridOptions}
        onColumnMoved={onColumnStateChanged} // Triggers on column rearrangement
        onColumnVisible={onColumnStateChanged} // Triggers on hide/show column
        onColumnResized={onColumnStateChanged} // Triggers on column resize
      />
      {/* {isOpen && (
        <ShowTransferModel
          isOpen={isOpen}
          onClose={onCloseTransferDetails}
          transfer={transfer}
          reFetchData={reFetchData}
          isSuperAdmin={isSuperAdmin}
          isStoreAdmin={isStoreAdmin}
          cognitoGroup={cognitoGroup}
        />
      )} */}
      {isEditTransferActive && !isRecievingFFLColumn && (
        <EditTransferModal
          isOpen={isEditTransferActive}
          onClose={onCloseEditTransferDetails}
          refetchData={reFetchData}
          transferToBeEdit={transferToBeEdit}
        />
      )}
    </Box>
  );
}

export function ShowTransferModel({
  isOpen,
  transfer,
  isSuperAdmin,
  isStoreAdmin,
  onClose,
  reFetchData,
  cognitoGroup
}: {
  isOpen: boolean;
  onClose: () => void;
  transfer: { id: string } | null;
  reFetchData: () => void;
  isSuperAdmin: boolean;
  isStoreAdmin: boolean;
  cognitoGroup: boolean;
}) {
  const dispatch = useDispatch();
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [transferDetails, setTransferDetails] = useState<TransferDetailsType>(
    TransferDetailsInitialState
  );
  const [transferStatus, setTransferStatus] = useState(
    transferDetails ? transferDetails?.TransferDetails.status : ''
  );
  const [editedTransferStatus, setEditedTransferStatus] = useState(
    transferDetails ? transferDetails?.TransferDetails.status : ''
  );

  useEffect(() => {
    if (transferDetails && transferDetails?.TransferDetails.status) {
      setEditedTransferStatus(transferDetails?.TransferDetails.status);
      setTransferStatus(transferDetails?.TransferDetails.status);
    }
  }, [transferDetails]);

  useEffect(() => {
    fetchTransferDetails();
  }, []);

  const fetchTransferDetails = async () => {
    setIsFetchingDetails(true);
    try {
      const response =
        (await getTranferDetails({ id: transfer?.id || '' })) || {};
      setTransferDetails(response.data);
      setIsFetchingDetails(false);
    } catch (error) {
      setIsFetchingDetails(false);
    }
  };

  const refreshTransferDetails = async () => {
    try {
      const response =
        (await getTranferDetails({ id: transfer?.id || '' })) || {};
      setTransferDetails(response.data);
    } catch (error) {
      console.log('error', error);
    }
  };

  const [isTransferStatusUpdating, setIsTransferStatusUpdating] =
    useState(false);
  const onchangeStatusDropdown = (value: Key) => {
    setEditedTransferStatus(value as string);
  };

  function getTransferStatus() {
    dispatch(callToGetTransferStatusWorkFlow({ id: transfer?.id as string }));
  }

  const handleClose = () => {
    onClose();
    dispatch(setCreateTransferData(null));
  };
  return (
    <>
      {isOpen && (
        <ModalComponent
          isOpen={isOpen}
          onClose={handleClose}
          footer={<></>}
          variant={2}
          title="Transfer Detail"
          maxWidth="1138px"
          className="transfer-details-modal"
        >
          <TransferDetail
            transferStatus={editedTransferStatus as string}
            onchangeStatusDropdown={onchangeStatusDropdown}
            data={transfer}
            isSuperAdmin={isSuperAdmin}
            cognitoGroup={cognitoGroup}
            isStoreAdmin={isStoreAdmin}
            isFetchingDetails={isFetchingDetails}
            transferDetails={transferDetails}
            getTransferStatus={getTransferStatus}
            refreshTransferDetails={refreshTransferDetails}
            handleClose={handleClose}
            refetchTranferQueue={reFetchData}
          />
        </ModalComponent>
      )}
    </>
  );
}

const formatPendingDocuments = (pendingDocuments: Array<string>) => {
  if (!pendingDocuments || !Array.isArray(pendingDocuments) || pendingDocuments.length === 0) {
    return '';
  }

  const formattedDocs = pendingDocuments.join('/');
  return `Pending ${formattedDocs}`;
};
